import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { 
  Package, Plus, AlertTriangle, CheckCircle, Users, ShoppingBag, 
  Baby, User, UserPlus, AlertCircle, Info, ArrowRight, History, Edit, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function for API calls
const apiCall = async (endpoint, options = {}) => {
  const token = localStorage.getItem('token');
  const defaultHeaders = {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
  
  const response = await fetch(`${API_URL}/api${endpoint}`, {
    ...options,
    headers: { ...defaultHeaders, ...options.headers }
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Erro desconhecido' }));
    throw error;
  }
  
  return response.json();
};

// Legacy mapping for backwards compatibility
const FAIXA_ETARIA_LABELS_LEGACY = {
  "bebe": "Bebé (0-2 anos)",
  "crianca": "Criança (3-10 anos)",
  "jovem": "Jovem (11-18 anos)",
  "adulto": "Adulto (18-64 anos)",
  "idoso": "Idoso (65+ anos)"
};

const SEXO_LABELS = {
  "M": "Masculino",
  "F": "Feminino",
  "Unissexo": "Unissexo"
};

const URGENCIA_OPTIONS = ["Baixa", "Normal", "Alta", "Crítica"];

const URGENCIA_COLORS = {
  "Crítica": "bg-red-500",
  "Alta": "bg-orange-500",
  "Normal": "bg-blue-500",
  "Baixa": "bg-slate-500"
};

export default function RegistoNecessidadesPage() {
  const [tiposRecursos, setTiposRecursos] = useState({});
  const [instituicoes, setInstituicoes] = useState([]);
  const [necessidades, setNecessidades] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Demographics state - faixas etárias dinâmicas
  const [faixasEtarias, setFaixasEtarias] = useState([]);
  const [faixasEtariasMap, setFaixasEtariasMap] = useState({});
  
  // Form state
  const [selectedInstituicao, setSelectedInstituicao] = useState('');
  const [perfilUtentes, setPerfilUtentes] = useState(null);
  const [loadingPerfil, setLoadingPerfil] = useState(false);
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [validacao, setValidacao] = useState(null);
  const [tamanhos, setTamanhos] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  
  // Form data
  const [form, setForm] = useState({
    tipo_recurso: '',
    sexo: '',
    faixa_etaria: '',
    tamanho: '',
    tipo: '',
    quantidade: 1,
    urgencia: 'Normal',
    justificacao: ''
  });

  // Load initial data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [tiposRes, instRes, necRes, faixasRes] = await Promise.all([
        apiCall('/tipos-recursos'),
        apiCall('/instituicoes?limit=500'),
        apiCall('/recursos/necessidades?limit=50'),
        apiCall('/demografia/faixas-etarias')
      ]);
      setTiposRecursos(tiposRes || {});
      setInstituicoes(instRes || []);
      setNecessidades(necRes || []);
      
      // Process faixas etárias
      const faixas = faixasRes || [];
      setFaixasEtarias(faixas);
      
      // Create mapping for quick lookup by codigo
      const faixasMap = {};
      faixas.forEach(f => {
        faixasMap[f.codigo] = f.nome;
        faixasMap[f.id] = f.nome;
      });
      // Add legacy mappings for backwards compatibility
      Object.assign(faixasMap, FAIXA_ETARIA_LABELS_LEGACY);
      setFaixasEtariasMap(faixasMap);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Load beneficiary profile when institution changes
  const loadPerfilUtentes = async (instituicaoId) => {
    if (!instituicaoId) {
      setPerfilUtentes(null);
      return;
    }
    
    setLoadingPerfil(true);
    try {
      const res = await apiCall(`/instituicoes/${instituicaoId}/perfil-utentes`);
      setPerfilUtentes(res);
    } catch (error) {
      console.error('Error loading beneficiary profile:', error);
      setPerfilUtentes(null);
    } finally {
      setLoadingPerfil(false);
    }
  };

  // Handle institution selection
  const handleInstituicaoChange = (value) => {
    setSelectedInstituicao(value);
    loadPerfilUtentes(value);
    // Reset form
    setForm(prev => ({
      ...prev,
      tipo_recurso: '',
      sexo: '',
      faixa_etaria: '',
      tamanho: '',
      tipo: '',
      quantidade: 1
    }));
    setValidacao(null);
    setTamanhos([]);
  };

  // Load sizes when age group changes
  const loadTamanhos = async (tipoRecurso, faixaEtaria) => {
    if (!tipoRecurso || !faixaEtaria) {
      setTamanhos([]);
      return;
    }
    
    try {
      const res = await apiCall(`/tipos-recursos/${tipoRecurso}/tamanhos?faixa_etaria=${faixaEtaria}`);
      setTamanhos(res?.tamanhos || []);
    } catch (error) {
      console.error('Error loading sizes:', error);
      setTamanhos([]);
    }
  };

  // Validate need
  const validateNeed = useCallback(async () => {
    if (!selectedInstituicao || !form.tipo_recurso) {
      setValidacao(null);
      return;
    }

    try {
      const res = await apiCall('/necessidades/validar', {
        method: 'POST',
        body: JSON.stringify({
          instituicao_id: selectedInstituicao,
          tipo_recurso: form.tipo_recurso,
          atributos: {
            sexo: form.sexo || undefined,
            faixa_etaria: form.faixa_etaria || undefined,
            tamanho: form.tamanho || undefined
          },
          quantidade: form.quantidade
        })
      });
      setValidacao(res);
    } catch (error) {
      console.error('Error validating:', error);
      setValidacao(null);
    }
  }, [selectedInstituicao, form]);

  // Auto-validate when form changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (form.tipo_recurso && form.sexo && form.faixa_etaria) {
        validateNeed();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [form.tipo_recurso, form.sexo, form.faixa_etaria, form.quantidade, validateNeed]);

  // Handle resource type change
  const handleTipoRecursoChange = (value) => {
    setForm(prev => ({
      ...prev,
      tipo_recurso: value,
      sexo: '',
      faixa_etaria: '',
      tamanho: '',
      tipo: ''
    }));
    setTamanhos([]);
    setValidacao(null);
  };

  // Handle age group change
  const handleFaixaEtariaChange = (value) => {
    setForm(prev => ({
      ...prev,
      faixa_etaria: value,
      tamanho: ''
    }));
    loadTamanhos(form.tipo_recurso, value);
  };

  // Submit need
  const handleSubmit = async () => {
    if (!selectedInstituicao || !form.tipo_recurso) {
      toast.error('Selecione a instituição e o tipo de recurso');
      return;
    }

    setFormLoading(true);
    try {
      // Build attributes object
      const atributos = {};
      const tipoConfig = tiposRecursos[form.tipo_recurso];
      
      if (tipoConfig?.atributos) {
        tipoConfig.atributos.forEach(attr => {
          if (form[attr.nome]) {
            atributos[attr.nome] = form[attr.nome];
          }
        });
      }

      await apiCall('/necessidades', {
        method: 'POST',
        body: JSON.stringify({
          instituicao_id: selectedInstituicao,
          tipo_recurso: form.tipo_recurso,
          quantidade_solicitada: form.quantidade,
          urgencia: form.urgencia,
          justificacao: form.justificacao,
          atributos
        })
      });

      toast.success('Necessidade registada com sucesso');
      setModalOpen(false);
      loadData();
      
      // Reset form
      setForm({
        tipo_recurso: '',
        sexo: '',
        faixa_etaria: '',
        tamanho: '',
        tipo: '',
        quantidade: 1,
        urgencia: 'Normal',
        justificacao: ''
      });
      setValidacao(null);
    } catch (error) {
      console.error('Error creating need:', error);
      if (error?.erros) {
        toast.error(error.erros.join('. '));
      } else {
        toast.error(error?.message || error?.detail || 'Erro ao registar necessidade');
      }
    } finally {
      setFormLoading(false);
    }
  };

  // Edit necessity
  const handleEditItem = (item) => {
    setEditingItem(item);
    setSelectedInstituicao(item.instituicao_id || '');
    setForm({
      tipo_recurso: item.tipo_recurso || '',
      sexo: item.sexo || item.atributos?.sexo || '',
      faixa_etaria: item.faixa_etaria || item.atributos?.faixa_etaria || '',
      tamanho: item.tamanho || item.atributos?.tamanho || '',
      tipo: item.atributos?.tipo || '',
      quantidade: item.quantidade_solicitada || item.quantidade || 1,
      urgencia: item.prioridade || item.urgencia || 'Normal',
      justificacao: item.observacoes || item.justificacao || ''
    });
    setModalOpen(true);
  };

  // Delete necessity
  const handleDeleteItem = async () => {
    if (!itemToDelete) return;
    
    try {
      await apiCall(`/recursos/necessidades/${itemToDelete.id}`, { method: 'DELETE' });
      toast.success('Necessidade eliminada com sucesso');
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
      loadData();
    } catch (error) {
      toast.error(error.detail || 'Erro ao eliminar necessidade');
    }
  };

  const openDeleteConfirm = (item) => {
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingItem(null);
    setForm({
      tipo_recurso: '', sexo: '', faixa_etaria: '', tamanho: '', tipo: '',
      quantidade: 1, urgencia: 'Normal', justificacao: ''
    });
    setValidacao(null);
  };

  // Get current tipo recurso config
  const currentTipoConfig = tiposRecursos[form.tipo_recurso];

  // Calculate available beneficiaries for current selection
  const getUtentesDisponiveis = () => {
    if (!perfilUtentes?.cruzado || !form.sexo || !form.faixa_etaria) return null;
    
    if (form.sexo === 'Unissexo') {
      return perfilUtentes.por_faixa_etaria?.[form.faixa_etaria] || 0;
    }
    
    return perfilUtentes.cruzado?.[form.sexo]?.[form.faixa_etaria] || 0;
  };

  const utentesDisponiveis = getUtentesDisponiveis();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Registo de Necessidades</h1>
          <p className="text-slate-600 mt-1">
            Registe necessidades de recursos validadas contra o perfil de utentes da instituição
          </p>
        </div>
        <Button onClick={() => setModalOpen(true)} data-testid="nova-necessidade-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nova Necessidade
        </Button>
      </div>

      {/* Info Card */}
      <Alert className="bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertTitle className="text-blue-800">Validação Automática</AlertTitle>
        <AlertDescription className="text-blue-700">
          O sistema valida automaticamente os pedidos contra o perfil de utentes da instituição.
          Por exemplo, não é possível pedir calçado masculino tamanho 42 se a instituição 
          só atende mulheres idosas.
        </AlertDescription>
      </Alert>

      {/* Recent Needs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-slate-600" />
            Necessidades Recentes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Instituição</TableHead>
                <TableHead>Tipo Recurso</TableHead>
                <TableHead>Atributos</TableHead>
                <TableHead>Qtd</TableHead>
                <TableHead>Urgência</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {necessidades.slice(0, 10).map((nec) => (
                <TableRow key={nec.id}>
                  <TableCell className="font-medium">{nec.instituicao_nome || '-'}</TableCell>
                  <TableCell>{nec.tipo_recurso_nome || nec.tipo_recurso || '-'}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {nec.atributos?.sexo && (
                        <Badge variant="outline" className="text-xs">
                          {SEXO_LABELS[nec.atributos.sexo] || nec.atributos.sexo}
                        </Badge>
                      )}
                      {nec.atributos?.faixa_etaria && (
                        <Badge variant="outline" className="text-xs">
                          {faixasEtariasMap[nec.atributos.faixa_etaria] || nec.atributos.faixa_etaria}
                        </Badge>
                      )}
                      {nec.atributos?.tamanho && (
                        <Badge variant="outline" className="text-xs">
                          Tam. {nec.atributos.tamanho}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{nec.quantidade_solicitada || nec.quantidade_necessaria}</TableCell>
                  <TableCell>
                    <Badge className={URGENCIA_COLORS[nec.urgencia || nec.prioridade] || 'bg-slate-500'}>
                      {nec.urgencia || nec.prioridade || 'Normal'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{nec.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEditItem(nec)} title="Editar">
                        <Edit className="w-4 h-4 text-blue-600" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openDeleteConfirm(nec)} title="Eliminar">
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {necessidades.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                    Nenhuma necessidade registada
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Modal - Nova/Editar Necessidade */}
      <Dialog open={modalOpen} onOpenChange={handleCloseModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Editar Necessidade' : 'Registar Nova Necessidade'}</DialogTitle>
            <DialogDescription>
              {editingItem ? 'Atualize os detalhes da necessidade' : 'Selecione a instituição e preencha os detalhes do recurso necessário'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-6 py-4">
            {/* Step 1: Select Institution */}
            <div className="space-y-3">
              <Label className="text-base font-semibold flex items-center gap-2">
                <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">1</span>
                Selecionar Instituição
              </Label>
              <Select value={selectedInstituicao} onValueChange={handleInstituicaoChange}>
                <SelectTrigger data-testid="select-instituicao">
                  <SelectValue placeholder="Selecione a instituição..." />
                </SelectTrigger>
                <SelectContent>
                  {instituicoes.map((inst) => (
                    <SelectItem key={inst.id} value={inst.id}>
                      {inst.nome} {inst.codigo_ea && `(${inst.codigo_ea})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Beneficiary Profile Card */}
            {selectedInstituicao && (
              <Card className="bg-slate-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Perfil de Utentes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingPerfil ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    </div>
                  ) : perfilUtentes?.total > 0 ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-600">{perfilUtentes.total}</div>
                          <div className="text-xs text-slate-500">Total Utentes</div>
                        </div>
                        <Separator orientation="vertical" className="h-12" />
                        <div className="flex gap-4">
                          <div className="text-center">
                            <div className="text-lg font-semibold text-blue-500">{perfilUtentes.por_sexo?.M || 0}</div>
                            <div className="text-xs text-slate-500">Masculino</div>
                          </div>
                          <div className="text-center">
                            <div className="text-lg font-semibold text-pink-500">{perfilUtentes.por_sexo?.F || 0}</div>
                            <div className="text-xs text-slate-500">Feminino</div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Age distribution */}
                      <div className="grid grid-cols-5 gap-2 text-center text-xs">
                        {Object.entries(perfilUtentes.por_faixa_etaria || {}).map(([faixa, count]) => (
                          <div key={faixa} className="bg-white p-2 rounded border">
                            <div className="font-semibold">{count}</div>
                            <div className="text-slate-500 truncate">{faixasEtariasMap[faixa]?.split(' ')[0] || faixa}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Alert variant="destructive" className="bg-amber-50 border-amber-200">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      <AlertDescription className="text-amber-800">
                        Esta instituição não tem utentes registados. A validação de necessidades não será aplicada.
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>
            )}

            {selectedInstituicao && (
              <>
                <Separator />

                {/* Step 2: Resource Type */}
                <div className="space-y-3">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">2</span>
                    Tipo de Recurso
                  </Label>
                  <Select value={form.tipo_recurso} onValueChange={handleTipoRecursoChange}>
                    <SelectTrigger data-testid="select-tipo-recurso">
                      <SelectValue placeholder="Selecione o tipo de recurso..." />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(tiposRecursos).map(([key, config]) => (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <Package className="h-4 w-4" />
                            {config.nome}
                            {config.requer_validacao_utentes && (
                              <Badge variant="outline" className="text-xs ml-2">Valida Utentes</Badge>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Step 3: Attributes */}
                {form.tipo_recurso && currentTipoConfig && (
                  <div className="space-y-3">
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">3</span>
                      Especificações
                      {currentTipoConfig.requer_validacao_utentes && (
                        <Badge className="bg-green-100 text-green-800 ml-2">Validação Ativa</Badge>
                      )}
                    </Label>

                    <div className="grid grid-cols-2 gap-4">
                      {/* Sex */}
                      {currentTipoConfig.atributos?.some(a => a.nome === 'sexo') && (
                        <div className="space-y-2">
                          <Label>Sexo *</Label>
                          <Select 
                            value={form.sexo} 
                            onValueChange={(v) => setForm(prev => ({ ...prev, sexo: v }))}
                          >
                            <SelectTrigger data-testid="select-sexo">
                              <SelectValue placeholder="Selecione..." />
                            </SelectTrigger>
                            <SelectContent>
                              {currentTipoConfig.atributos.find(a => a.nome === 'sexo')?.opcoes.map(opt => (
                                <SelectItem key={opt} value={opt}>
                                  {SEXO_LABELS[opt] || opt}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* Age Group - Dynamic from API */}
                      {currentTipoConfig.atributos?.some(a => a.nome === 'faixa_etaria') && (
                        <div className="space-y-2">
                          <Label>Faixa Etária *</Label>
                          <Select 
                            value={form.faixa_etaria} 
                            onValueChange={handleFaixaEtariaChange}
                          >
                            <SelectTrigger data-testid="select-faixa-etaria">
                              <SelectValue placeholder="Selecione..." />
                            </SelectTrigger>
                            <SelectContent>
                              {faixasEtarias.length > 0 ? (
                                faixasEtarias.map(faixa => (
                                  <SelectItem key={faixa.id} value={faixa.codigo}>
                                    {faixa.nome}
                                  </SelectItem>
                                ))
                              ) : (
                                // Fallback to static options if API fails
                                currentTipoConfig.atributos.find(a => a.nome === 'faixa_etaria')?.opcoes.map(opt => (
                                  <SelectItem key={opt} value={opt}>
                                    {faixasEtariasMap[opt] || opt}
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* Size (dynamic) */}
                      {currentTipoConfig.atributos?.some(a => a.nome === 'tamanho') && tamanhos.length > 0 && (
                        <div className="space-y-2">
                          <Label>Tamanho *</Label>
                          <Select 
                            value={form.tamanho} 
                            onValueChange={(v) => setForm(prev => ({ ...prev, tamanho: v }))}
                          >
                            <SelectTrigger data-testid="select-tamanho">
                              <SelectValue placeholder="Selecione o tamanho..." />
                            </SelectTrigger>
                            <SelectContent>
                              {tamanhos.map(tam => (
                                <SelectItem key={tam} value={tam}>{tam}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* Type (clothing, shoes, etc) */}
                      {currentTipoConfig.atributos?.some(a => a.nome === 'tipo') && (
                        <div className="space-y-2">
                          <Label>Tipo de Peça</Label>
                          <Select 
                            value={form.tipo} 
                            onValueChange={(v) => setForm(prev => ({ ...prev, tipo: v }))}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Opcional..." />
                            </SelectTrigger>
                            <SelectContent>
                              {currentTipoConfig.atributos.find(a => a.nome === 'tipo')?.opcoes.map(opt => (
                                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>

                    {/* Available beneficiaries indicator */}
                    {utentesDisponiveis !== null && (
                      <Alert className={utentesDisponiveis > 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
                        {utentesDisponiveis > 0 ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : (
                          <AlertTriangle className="h-4 w-4 text-red-600" />
                        )}
                        <AlertDescription className={utentesDisponiveis > 0 ? 'text-green-800' : 'text-red-800'}>
                          {utentesDisponiveis > 0 ? (
                            <>
                              A instituição tem <strong>{utentesDisponiveis} utentes</strong> que correspondem 
                              a esta seleção ({SEXO_LABELS[form.sexo]}, {faixasEtariasMap[form.faixa_etaria] || form.faixa_etaria}).
                            </>
                          ) : (
                            <>
                              A instituição <strong>não tem utentes</strong> que correspondam a esta seleção.
                              Não é possível registar esta necessidade.
                            </>
                          )}
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                )}

                {/* Step 4: Quantity and Urgency */}
                {form.tipo_recurso && (
                  <div className="space-y-3">
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">4</span>
                      Quantidade e Urgência
                    </Label>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Quantidade *</Label>
                        <Input
                          type="number"
                          min="1"
                          max={utentesDisponiveis || 9999}
                          value={form.quantidade}
                          onChange={(e) => setForm(prev => ({ ...prev, quantidade: parseInt(e.target.value) || 1 }))}
                          data-testid="input-quantidade"
                        />
                        {utentesDisponiveis && form.quantidade > utentesDisponiveis && (
                          <p className="text-xs text-amber-600">
                            ⚠️ Quantidade superior aos utentes disponíveis ({utentesDisponiveis})
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label>Urgência</Label>
                        <Select 
                          value={form.urgencia} 
                          onValueChange={(v) => setForm(prev => ({ ...prev, urgencia: v }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {URGENCIA_OPTIONS.map(urg => (
                              <SelectItem key={urg} value={urg}>{urg}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Justificação</Label>
                      <Textarea
                        placeholder="Descreva a razão da necessidade..."
                        value={form.justificacao}
                        onChange={(e) => setForm(prev => ({ ...prev, justificacao: e.target.value }))}
                        rows={3}
                      />
                    </div>
                  </div>
                )}

                {/* Validation Result */}
                {validacao && (
                  <Card className={validacao.valido ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        {validacao.valido ? (
                          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                        ) : (
                          <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                        )}
                        <div className="space-y-2">
                          <p className={validacao.valido ? 'text-green-800 font-medium' : 'text-red-800 font-medium'}>
                            {validacao.valido ? 'Pedido válido' : 'Pedido inválido'}
                          </p>
                          
                          {validacao.erros?.length > 0 && (
                            <ul className="text-sm text-red-700 list-disc pl-4">
                              {validacao.erros.map((erro, i) => (
                                <li key={i}>{erro}</li>
                              ))}
                            </ul>
                          )}
                          
                          {validacao.avisos?.length > 0 && (
                            <ul className="text-sm text-amber-700 list-disc pl-4">
                              {validacao.avisos.map((aviso, i) => (
                                <li key={i}>{aviso}</li>
                              ))}
                            </ul>
                          )}
                          
                          {validacao.quantidade_maxima < form.quantidade && (
                            <p className="text-sm text-blue-700">
                              💡 Quantidade máxima recomendada: <strong>{validacao.quantidade_maxima}</strong>
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={
                formLoading || 
                !selectedInstituicao || 
                !form.tipo_recurso || 
                (validacao && !validacao.valido) ||
                (utentesDisponiveis !== null && utentesDisponiveis === 0)
              }
              data-testid="submit-necessidade-btn"
            >
              {formLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  A guardar...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {editingItem ? 'Atualizar Necessidade' : 'Registar Necessidade'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Confirmar Eliminação</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja eliminar esta necessidade?
              {itemToDelete && (
                <div className="mt-2 p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium">{itemToDelete.tipo_recurso_nome || itemToDelete.tipo_recurso}</p>
                  <p className="text-sm text-slate-500">
                    {itemToDelete.instituicao_nome} • Quantidade: {itemToDelete.quantidade_solicitada || itemToDelete.quantidade_necessaria}
                  </p>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDeleteItem}>Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
